import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "../lib/api";
import { isDesktop, getDesktopUpdateInfo } from "../lib/desktop";
import { APP_BUILD } from "../lib/build-info";

/**
 * Drives the sidebar update badge.
 *
 * Web: polls `GET /app/version` (written by `scripts/deploy.sh`). While a
 * deploy is rolling out the badge shows a gentle "updating" heads-up; once
 * the shipped `build` differs from the one baked into this bundle
 * (`__APP_BUILD__`) it flips to "update available" and offers a reload.
 *
 * Desktop (Tauri): polls the signed updater once an hour and lights the
 * badge when a newer build is ready to install.
 */
export type UpdateState =
  | { kind: "none" }
  | { kind: "deploying" }
  | { kind: "web-ready"; version: string | null }
  | { kind: "desktop-ready"; version?: string };

const WEB_POLL_MS = 90_000;
const DESKTOP_POLL_MS = 60 * 60 * 1000; // hourly

export function useAppUpdate(): UpdateState {
  const [state, setState] = useState<UpdateState>({ kind: "none" });
  // Once an update is confirmed available we keep the badge lit even if a
  // later poll blips, so the user never loses the prompt mid-session.
  const lockedRef = useRef(false);

  const pollWeb = useCallback(async () => {
    if (lockedRef.current) return;
    try {
      const res = await api.getAppVersion();
      if (res.build && res.build !== APP_BUILD) {
        lockedRef.current = true;
        setState({ kind: "web-ready", version: res.version });
      } else if (res.deploying) {
        setState({ kind: "deploying" });
      } else {
        setState({ kind: "none" });
      }
    } catch {
      /* offline / transient — leave current state untouched */
    }
  }, []);

  const pollDesktop = useCallback(async () => {
    if (lockedRef.current) return;
    const info = await getDesktopUpdateInfo();
    if (info.available) {
      lockedRef.current = true;
      setState({ kind: "desktop-ready", version: info.version });
    }
  }, []);

  useEffect(() => {
    if (isDesktop) {
      void pollDesktop();
      const id = window.setInterval(() => void pollDesktop(), DESKTOP_POLL_MS);
      return () => window.clearInterval(id);
    }
    // Web build: only meaningful in production, where a real build token is
    // baked in. Dev builds use `build === 'dev'` and would false-positive.
    if (!import.meta.env.PROD) return;
    void pollWeb();
    const id = window.setInterval(() => void pollWeb(), WEB_POLL_MS);
    const onFocus = () => void pollWeb();
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [pollWeb, pollDesktop]);

  return state;
}
